<script lang="ts" setup>
    import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
    import type { PropType } from 'vue'
    import { generateUniqueKey } from '#shared/unique-key'
    import { useDialogStack } from '~/composables/dialog-stack'
    import { RequestError } from '~/composables/request'
    import { ValidationError } from '~/composables/validation'

    const props = defineProps({
        modelValue: { type: Boolean, required: true },
        title: String,
        confirmLabel: { type: String, default: 'OK' },
        confirmColor: { type: String, default: 'primary' },
        cancelLabel: { type: String, default: 'Cancel' },
        busy: Boolean,
        confirmDisabled: Boolean,
        error: String,
        minWidth: { type: String, default: '380px' },
        confirmCommand: {
            type: Object as PropType<{ execute: () => Promise<void> }>,
            default: null,
        },
        prefill: {
            type: Object as PropType<Record<string, string>>,
            default: () => ({}),
        },
    })

    const emit = defineEmits(['update:modelValue', 'confirm'])

    const model = computed({
        get: () => props.modelValue,
        set: (val) => emit('update:modelValue', val),
    })

    const card = ref<{ $el: HTMLElement } | null>(null)

    function applyPrefill(): void {
        const root = card.value?.$el
        if (!root) {
            return
        }
        for (const [key, value] of Object.entries(props.prefill)) {
            const field = root.querySelector<HTMLInputElement | HTMLTextAreaElement>(
                `input[name="${key}"], textarea[name="${key}"]`,
            )
            if (!field) {
                continue
            }
            const isTextField =
                field instanceof HTMLTextAreaElement
                || !field.type
                || field.type === 'text'
            if (!isTextField) {
                continue
            }
            field.value = value
            field.dispatchEvent(new Event('input', { bubbles: true }))
        }
    }

    watch(
        () => props.modelValue,
        async (open) => {
            if (!open) {
                return
            }
            await nextTick()
            applyPrefill()
        },
    )

    const dialogStack = useDialogStack()
    const stackKey = generateUniqueKey()

    watch(
        () => props.modelValue,
        (open) => {
            if (open) {
                dialogStack.open(stackKey)
                return
            }
            dialogStack.close(stackKey)
        },
        { immediate: true },
    )

    onBeforeUnmount(() => {
        dialogStack.close(stackKey)
    })

    const commandRunning = ref(false)
    const confirmCommandError = ref<string | null>(null)

    const isBusy = computed((): boolean => props.busy || commandRunning.value)
    const displayError = computed(() => confirmCommandError.value ?? props.error ?? null)

    async function onConfirm() {
        if (!props.confirmCommand) {
            emit('confirm')
            return
        }
        confirmCommandError.value = null
        commandRunning.value = true
        try {
            await props.confirmCommand.execute()
            model.value = false
        }
        catch (err) {
            if (err instanceof RequestError || err instanceof ValidationError) {
                confirmCommandError.value = err.message
            }
            else {
                confirmCommandError.value = 'An unexpected error occurred'
            }
        }
        finally {
            commandRunning.value = false
        }
    }
</script>

<template>
    <q-dialog
        v-model="model"
        @keyup.enter="onConfirm"
    >
        <q-card
            ref="card"
            :style="`min-width: ${minWidth}`"
        >
            <q-card-section>
                <div class="row items-center no-wrap">
                    <div
                        v-if="title"
                        class="text-h6"
                    >
                        {{ title }}
                    </div>
                    <q-space />
                    <q-spinner
                        size="1.25em"
                        color="grey-7"
                        data-testid="bz-dialog-busy"
                        :class="{ invisible: !isBusy }"
                    />
                </div>
            </q-card-section>
            <q-card-section class="q-gutter-sm">
                <slot />
            </q-card-section>
            <q-card-section
                v-if="displayError"
                class="text-negative q-pt-none"
                data-testid="bz-dialog-error"
            >
                {{ displayError }}
            </q-card-section>
            <q-card-actions align="right">
                <slot name="commands" />
                <q-btn
                    v-close-popup
                    flat
                    :label="cancelLabel"
                    data-testid="bz-dialog-cancel"
                />
                <q-btn
                    :label="confirmLabel"
                    data-testid="bz-dialog-confirm"
                    :color="confirmColor"
                    unelevated
                    :disable="confirmDisabled || isBusy"
                    @click="onConfirm"
                />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>
